/**
 * LiveGameObjectLayer -- manages the lifecycle of PixiJS display objects for
 * the live preview panel.
 *
 * Simplified version of the scene-view GameObjectLayer:
 *   - NO editor gizmos, selection highlights, or lock indicators
 *   - NO focused-container filtering
 *   - WITH real texture loading via getOrCreateObjectUrl (File System Access API)
 *   - Renders objects FLAT (all at the same Container level, using computed
 *     world positions) rather than nested PixiJS containers
 *
 * Reuses the existing renderer classes from scene-view/pixi/renderers/.
 */
import { Container } from 'pixi.js';
import type { GameObject } from '@core/types/gameobject.types';
import { SpriteRenderer } from '../../scene-view/pixi/renderers/SpriteRenderer';
import { TextRenderer } from '../../scene-view/pixi/renderers/TextRenderer';
import { GraphicsRenderer } from '../../scene-view/pixi/renderers/GraphicsRenderer';
import { SpineRenderer } from '../../scene-view/pixi/renderers/SpineRenderer';
import { PlaceholderRenderer } from '../../scene-view/pixi/renderers/PlaceholderRenderer';
import { getOrCreateObjectUrl, releaseObjectUrl } from '@hooks/useAssetUrl';

interface RenderedObject {
  container: Container;
  type: string;
  /** Hash of properties that affect rendering -- triggers recreation on change. */
  version: string;
  /** Tracked so we can call releaseObjectUrl on cleanup. */
  assetPath: string | null;
}

export class LiveGameObjectLayer {
  readonly container: Container;
  private objectMap = new Map<string, RenderedObject>();
  /** Multiplier to separate sortOrder from hierarchy tiebreaker in zIndex. */
  private static readonly SORT_ORDER_MULTIPLIER = 100_000;

  constructor(parentContainer: Container) {
    this.container = parentContainer;
    this.container.sortableChildren = true;
  }

  /**
   * Sync the PixiJS display objects with the current scene store state.
   *
   * @param gameObjects       All game objects in the scene.
   * @param isEffectivelyVisible  Checks visibility including parent chain.
   */
  async sync(
    gameObjects: Record<string, GameObject>,
    isEffectivelyVisible: (id: string) => boolean,
  ): Promise<void> {
    const currentIds = new Set(Object.keys(gameObjects));
    const hierarchyOrder = LiveGameObjectLayer.buildHierarchyOrder(gameObjects);

    // Phase 1: Remove deleted objects
    for (const [id, entry] of this.objectMap) {
      if (!currentIds.has(id)) {
        this.container.removeChild(entry.container);
        entry.container.destroy({ children: true });
        if (entry.assetPath) releaseObjectUrl(entry.assetPath);
        this.objectMap.delete(id);
      }
    }

    // Phase 2: Add / update objects
    for (const obj of Object.values(gameObjects)) {
      const newVersion = LiveGameObjectLayer.computeVersion(obj);
      const existing = this.objectMap.get(obj.id);

      if (existing && existing.version === newVersion) {
        // Version unchanged -- just update transform and visibility
        this.applyTransform(existing.container, obj, gameObjects);
        existing.container.visible = isEffectivelyVisible(obj.id);
        existing.container.zIndex = ((obj.transform.sortOrder ?? 0) * LiveGameObjectLayer.SORT_ORDER_MULTIPLIER) + (hierarchyOrder.get(obj.id) ?? 0);
        continue;
      }

      // For text objects, update in-place instead of destroying and recreating
      if (existing && obj.type === 'text') {
        TextRenderer.update(existing.container, obj);
        this.applyTransform(existing.container, obj, gameObjects);
        existing.container.visible = isEffectivelyVisible(obj.id);
        existing.container.zIndex = ((obj.transform.sortOrder ?? 0) * LiveGameObjectLayer.SORT_ORDER_MULTIPLIER) + (hierarchyOrder.get(obj.id) ?? 0);
        existing.version = newVersion;
        continue;
      }

      if (existing) {
        // Version changed -- destroy and recreate
        this.container.removeChild(existing.container);
        existing.container.destroy({ children: true });
        if (existing.assetPath) releaseObjectUrl(existing.assetPath);
        this.objectMap.delete(obj.id);
      }

      // Create new display object
      const { container, assetPath } = await this.createForType(obj);
      this.applyTransform(container, obj, gameObjects);
      container.visible = isEffectivelyVisible(obj.id);
      container.zIndex = ((obj.transform.sortOrder ?? 0) * LiveGameObjectLayer.SORT_ORDER_MULTIPLIER) + (hierarchyOrder.get(obj.id) ?? 0);
      this.container.addChild(container);

      this.objectMap.set(obj.id, {
        container,
        type: obj.type,
        version: newVersion,
        assetPath,
      });
    }
  }

  /**
   * Look up the PixiJS Container for a given game object ID.
   */
  getDisplayObject(id: string): Container | undefined {
    return this.objectMap.get(id)?.container;
  }

  // ── Interactivity & Visibility ─────────────────────────────────

  /**
   * Make a rendered game object clickable during play mode.
   */
  setInteractive(gameObjectId: string, onClick: () => void): void {
    const entry = this.objectMap.get(gameObjectId);
    if (!entry) return;
    entry.container.eventMode = 'static';
    entry.container.cursor = 'pointer';
    entry.container.removeAllListeners('pointerdown');
    entry.container.on('pointerdown', onClick);
  }

  /**
   * Remove click handling from a rendered game object.
   */
  removeInteractive(gameObjectId: string): void {
    const entry = this.objectMap.get(gameObjectId);
    if (!entry) return;
    entry.container.eventMode = 'none';
    entry.container.cursor = 'default';
    entry.container.removeAllListeners('pointerdown');
  }

  /**
   * Show or hide a specific rendered object (e.g. hide symbol placeholders
   * during play mode).
   */
  setVisible(gameObjectId: string, visible: boolean): void {
    const entry = this.objectMap.get(gameObjectId);
    if (!entry) return;
    entry.container.visible = visible;
  }

  /**
   * Change opacity of a rendered object (e.g. dim buttons during non-idle
   * phases).
   */
  setAlpha(gameObjectId: string, alpha: number): void {
    const entry = this.objectMap.get(gameObjectId);
    if (!entry) return;
    entry.container.alpha = alpha;
  }

  /** Destroy all managed display objects and release tracked asset URLs. */
  destroy(): void {
    for (const [, entry] of this.objectMap) {
      entry.container.destroy({ children: true });
      if (entry.assetPath) releaseObjectUrl(entry.assetPath);
    }
    this.objectMap.clear();
  }

  // ── Private: Object Creation ─────────────────────────────────────

  private async createForType(
    obj: GameObject,
  ): Promise<{ container: Container; assetPath: string | null }> {
    switch (obj.type) {
      case 'sprite': {
        const spriteComp = obj.components.find(c => c.type === 'sprite-renderer');
        const texturePath = (spriteComp?.data?.texturePath as string) ?? null;
        if (texturePath) {
          try {
            const blobUrl = await getOrCreateObjectUrl(texturePath);
            const container = await SpriteRenderer.create(obj, blobUrl);
            return { container, assetPath: texturePath };
          } catch {
            // Texture load failed -- fall through to placeholder
            return { container: PlaceholderRenderer.create(obj), assetPath: null };
          }
        }
        return { container: PlaceholderRenderer.create(obj), assetPath: null };
      }

      case 'text':
        return { container: TextRenderer.create(obj), assetPath: null };

      case 'graphics':
        return { container: GraphicsRenderer.create(obj), assetPath: null };

      case 'spine':
        return { container: SpineRenderer.create(obj), assetPath: null };

      case 'container':
      case 'empty':
      case 'cascade-slot':
      case 'backend-slot-game':
        // Structural containers: invisible in live preview (children render flat)
        return { container: new Container(), assetPath: null };
      default:
        return { container: PlaceholderRenderer.create(obj), assetPath: null };
    }
  }

  // ── Private: Transform ───────────────────────────────────────────

  private applyTransform(
    container: Container,
    obj: GameObject,
    allGameObjects: Record<string, GameObject>,
  ): void {
    // All objects are rendered flat, so we compute world position by
    // walking the parent chain.
    const worldPos = this.getWorldPosition(obj, allGameObjects);
    container.position.set(worldPos.x, worldPos.y);
    container.rotation = ((obj.transform.rotation ?? 0) * Math.PI) / 180;
    container.scale.set(
      obj.transform.scale.x ?? 1,
      obj.transform.scale.y ?? 1,
    );
  }

  private getWorldPosition(
    obj: GameObject,
    gameObjects: Record<string, GameObject>,
  ): { x: number; y: number } {
    let x = obj.transform.position.x;
    let y = obj.transform.position.y;
    let current = obj;

    while (current.parentId) {
      const parent = gameObjects[current.parentId];
      if (!parent) break;
      x += parent.transform.position.x;
      y += parent.transform.position.y;
      current = parent;
    }

    return { x, y };
  }

  // ── Private: Versioning ──────────────────────────────────────────

  /**
   * Compute a version hash for a game object. When this changes between
   * syncs the display object is destroyed and recreated (e.g. a placeholder
   * becomes a real sprite once a texture is assigned).
   */
  private static computeVersion(obj: GameObject): string {
    const spriteData = obj.components.find(
      c => c.type === 'sprite-renderer',
    )?.data as Record<string, unknown> | undefined;
    const texturePath = (spriteData?.texturePath as string) ?? '';

    // Include text-renderer data so text property changes trigger re-render
    const textData = obj.components.find(
      c => c.type === 'text-renderer',
    )?.data as Record<string, unknown> | undefined;
    const textHash = textData
      ? `${textData.text ?? ''}|${textData.fontSize ?? ''}|${textData.color ?? ''}|${textData.fontFamily ?? ''}|${textData.fontWeight ?? ''}|${textData.fontStyle ?? ''}|${textData.textAlign ?? ''}|${textData.letterSpacing ?? ''}|${textData.wordWrap ?? ''}|${textData.lineHeight ?? ''}`
      : '';

    return `${obj.type}|${texturePath}|${obj.name}|${textHash}`;
  }

  /**
   * Build a hierarchy-order map via depth-first traversal of the scene tree.
   * Objects later in childIds[] (lower in the Hierarchy panel) get a higher
   * index and render on top when sortOrder is the same.
   * Matches the ordering logic in SceneCanvas.tsx.
   */
  private static buildHierarchyOrder(
    gameObjects: Record<string, GameObject>,
  ): Map<string, number> {
    const order = new Map<string, number>();
    let index = 0;
    const walk = (id: string) => {
      const obj = gameObjects[id];
      if (!obj) return;
      order.set(id, index++);
      for (const childId of obj.childIds) {
        walk(childId);
      }
    };
    for (const obj of Object.values(gameObjects)) {
      if (!obj.parentId) walk(obj.id);
    }
    return order;
  }
}
